import { toast } from "sonner";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Text } from "@/components/ui/Text";
import { SimpleTooltip, TooltipProvider } from "@/components/ui/Tooltip";
import { useOrganization, useProject, useSession } from "@/contexts/Auth";
import { useSdkClient } from "@/contexts/Sdk";
import type { ManagedAgent } from "@gram/client/models/components/managedagent.js";
import { GramError } from "@gram/client/models/errors/gramerror.js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router";
import {
  agentPolicyGrantsFromDraft,
  invalidateAgentPolicy,
  type AgentPolicyDraft,
} from "../agent-policy-grants";
import {
  buildRequestedGrants,
  delegableGrantKey,
  requestNarrowsPolicy,
} from "../agent-api-key-grants";
import {
  discoverKeyServerGrants,
  discoverWildcardKeyGrants,
} from "../agent-key-discovery";
import {
  discoverUnscopedGrants,
  HOOKS_INGEST_SCOPE,
  hooksIngestKeyGrants,
  hooksIngestPolicyGrant,
} from "../agent-hook-events";
import {
  agentGatewayURL,
  isCredentialSafe,
  mintInstallCommand,
} from "./gateway";
import { StepInstall } from "./StepInstall";
import {
  StepServers,
  type AllServersRefusal,
  type ServerSelection,
} from "./StepServers";
import { keyCredentialChoices } from "./keyCredentialChoices";
import { StepVerify, type VerifyState } from "./StepVerify";
import { CredentialPicker } from "./CredentialPicker";
import { SignInPath } from "./SignInPath";
import { StepWorkload } from "./StepWorkload";
import { useWorkloadSetup } from "./useWorkloadSetup";
import { hasPlatformSetup, platformSetup } from "./platformSetups";
import {
  DEVICE_AGENT_SYNC_SCOPE,
  GITHUB_ACTIONS_KEY,
  jobScope,
  repositoryName,
  type JobScope,
} from "./githubActions";
import type { AgentPolicyGrantForm } from "@gram/client/models/components/agentpolicygrantform.js";
import { CUSTOM_PLATFORM_KEY } from "./CustomSetupInstructions";
import {
  CONNECT_SCOPE,
  grantChanges,
  grantedServers,
  reachCoversProject,
  wildcardConnectGrant,
  type GrantedServer,
  type WildcardConnectGrant,
} from "@/pages/agents/agentPolicyEdit";
import { useAgentsCreatePolicyGrantMutation } from "@gram/client/react-query/agentsCreatePolicyGrant.js";
import { useAgentsDeletePolicyGrantMutation } from "@gram/client/react-query/agentsDeletePolicyGrant.js";
import { invalidateAllListAPIKeys } from "@gram/client/react-query/listAPIKeys.js";
import type { ExtendAgentKeyGrantForm } from "@gram/client/models/components/extendagentkeygrantform.js";
import { useRenameAgentMutation } from "@gram/client/react-query/renameAgent.js";
import { useAgents } from "@gram/client/react-query/agents.js";
import { useUpstreamAuthEnforcement } from "@/hooks/useUpstreamAuthEnforcement";
import type { AgentPolicyGrant } from "@gram/client/models/components/agentpolicygrant.js";
import type { AgentPolicyGrantCheck } from "@gram/client/models/components/agentpolicygrantcheck.js";
import { useAgentAdmissions } from "@/pages/agents/agentAdmissions";
import {
  catalogEntryForIssuer,
  SUBJECT_CHOICE,
  useCatalogEntries,
  valuesFromIssuer,
  valuesFromSubject,
} from "./workloadCatalog";
import { useRBAC } from "@/hooks/useRBAC";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useServerInventory, type InventoryServer } from "./useServerInventory";
import { useOwnerConnections } from "./useOwnerConnections";
import { StepAuthentication } from "./StepAuthentication";
import { connectionSelectable } from "./ownerConnections";
import { attachOwnerConnections } from "./ownerConnections";
import {
  WizardFooter,
  WizardStepHeader,
  WizardStepper,
  WizardSummary,
} from "./WizardChrome";

/**
 * Creating an agent identity here is only ever a means to provisioning one
 * somewhere else, so the flow runs to that end: name it, choose what it may
 * reach, pick how it proves who it is, hand the runtime its credential, and
 * watch for the first call.
 *
 * The identity is created at the end of the choices, not the start: everything
 * before Install is still a draft the user can abandon without leaving a
 * half-made agent behind.
 */

/** Each step's name, and the one or two words it shrinks to in a full row. */
const STEPS = [
  { label: "Name", short: "Name" },
  { label: "MCP Selection", short: "MCPs" },
  { label: "MCP sign-in", short: "MCP sign-in" },
  { label: "Agent sign-in", short: "Agent sign-in" },
  { label: "Install", short: "Install" },
  { label: "Verify", short: "Verify" },
];

/** How the agent proves who it is. */
type Credential = "api-key" | "workload";

/** Keys live 90 days unless the agent page rotates them sooner. */
const KEY_LIFETIME_DAYS = 90;

// Stopgap (AIM-862): an agent that may connect to servers cannot sign in to
// them without mcp:auth, and nothing here can choose auth clients yet, so it is
// given all of them. Replace this with an auth client selector, made an
// explicit choice when the agent is created, and grant only the clients chosen
// there.
const AUTH_GRANT = {
  effect: "allow",
  scope: "mcp:auth",
  selector: { resourceKind: "mcp_auth_client", resourceId: "*" },
} as const;

/** The most grants agents.checkPolicyGrants answers in one request. */
const CHECK_BATCH_SIZE = 100;

/**
 * What the agent API accepts as a name. The input's own `maxLength` stops
 * typing past it but not a value this file writes into state, which would be
 * refused on submit with nothing on screen to explain why.
 */
const NAME_LIMIT = 120;

/** As many grants as one request may add to an agent's keys. */
const EXTEND_KEYS_BATCH = 100;

/**
 * The preset name, with a number appended while the organization already
 * has one: the server refuses a duplicate, and a prefilled field that is
 * refused on submit is worse than no prefill at all.
 */
function freeName(base: string, taken: Set<string>): string {
  const plain = base.slice(0, NAME_LIMIT);
  if (!taken.has(plain)) return plain;
  // The suffix is what makes the name free, so the base gives up the
  // characters it needs rather than the suffix falling off the end.
  const numbered = (suffix: number) =>
    `${base.slice(0, NAME_LIMIT - ` ${suffix}`.length)} ${suffix}`;
  // Every candidate ends in its own number, so they are all distinct and one
  // of the first `taken.size + 1` of them must be free: the loop ends on a
  // name the server will accept rather than giving up on the occupied one.
  let suffix = 2;
  while (taken.has(numbered(suffix))) suffix++;
  return numbered(suffix);
}

/**
 * Why some of these grants cannot be written, from checkPolicyGrants' answers
 * in the same order, or null when every one can.
 */
function grantRefusal(
  grants: AgentPolicyGrantForm[],
  answers: AgentPolicyGrantCheck[],
): string | null {
  const refused = grants.filter((_, index) => answers[index]?.allowed !== true);
  if (refused.length === 0) return null;
  const scopes = [...new Set(refused.map((grant) => grant.scope))].join(
    " and ",
  );
  return answers.some(
    (answer) => answer.allowed !== true && answer.limitedBy === "owner",
  )
    ? `This agent's owner does not hold ${scopes}, so the agent cannot be given it. Ask an organization administrator to give the owner that permission, or to run this setup.`
    : `You do not hold ${scopes}, so you cannot give it to this agent. Ask an organization administrator to run this setup.`;
}

/**
 * The summary row a platform's field fills in, so typing into the field lights
 * up the row it changes. Fields with no row of their own light nothing.
 */
const SUMMARY_ROW_FOR_FIELD = new Map<string, string>([
  ["org_id", "Anthropic organization"],
  ["owner", "Repository"],
  ["repo", "Repository"],
  [SUBJECT_CHOICE, "Jobs"],
  ["branch", "Branch"],
  ["environment", "Environment"],
  ["project", "Cluster"],
  ["location", "Cluster"],
  ["cluster", "Cluster"],
  ["namespace", "Service account"],
  ["service_account", "Service account"],
  ["name", "Platform"],
  ["issuer", "Issuer"],
]);

/**
 * The rail's row for what a GitHub Actions rule narrows the repository to:
 * its pull requests, or a branch or environment once it is named. None for
 * the whole repository.
 */
function githubActionsScopeRow(
  scope: JobScope,
): { label: string; value: ReactNode }[] {
  switch (scope.kind) {
    case "repository":
      return [];
    case "pull_request":
      return [{ label: "Jobs", value: "Pull requests" }];
    case "branch":
    case "environment":
      if (scope.name === "") return [];
      return [
        {
          label: scope.kind === "branch" ? "Branch" : "Environment",
          value: <code className="text-xs break-all">{scope.name}</code>,
        },
      ];
  }
}

/**
 * Whether a grant the agent holds already covers one it would be given, by
 * the rule the server's selector matching applies.
 */
function grantCovers(
  held: AgentPolicyGrant,
  wanted: AgentPolicyGrantForm,
): boolean {
  return (
    held.effect === "allow" &&
    held.scope === wanted.scope &&
    requestNarrowsPolicy(held.selector, wanted.selector)
  );
}

/** Grants with each one kept once, in their first order. */
function uniqueGrants(grants: AgentPolicyGrantForm[]): AgentPolicyGrantForm[] {
  return [
    ...new Map(
      grants.map((grant) => [delegableGrantKey(grant), grant]),
    ).values(),
  ];
}

/**
 * What the agent can do, in words, with the scopes it will be granted one
 * hover or focus away for the reader who needs to match them to a policy.
 */
function GrantedScopes({
  scopes,
  children,
}: {
  scopes: string[];
  children: ReactNode;
}): JSX.Element {
  // Its own provider: the summary is rendered on its own in tests, and a
  // tooltip without one throws rather than degrades.
  return (
    <TooltipProvider>
      <SimpleTooltip
        tooltip={
          <ul className="space-y-0.5 text-left font-mono text-xs">
            {scopes.map((scope) => (
              <li key={scope}>{scope}</li>
            ))}
          </ul>
        }
      >
        <span
          tabIndex={0}
          className="cursor-default underline decoration-dotted underline-offset-4"
        >
          {children}
        </span>
      </SimpleTooltip>
    </TooltipProvider>
  );
}

export function CredentialsWizard({
  agent,
  initialPlatformKey,
  editing = false,
  grants,
  onDone,
  onBusy,
  verifyPollMs = 5000,
}: {
  /**
   * The platform this flow was opened for, from a link that names one. The
   * credential question is then already answered, and the flow starts where
   * the reader still has something to decide.
   */
  initialPlatformKey?: string;
  /**
   * Change what an agent already is — its name, its reach — rather than
   * provision it again. The credential steps are not part of that: a key is
   * issued from the agent's own page, and a platform from this flow.
   */
  editing?: boolean;
  /** The servers the agent already reaches, for the editing flow. */
  grants?: AgentPolicyGrant[];
  /**
   * The agent being provisioned again. While creating one there is no agent:
   * the identity does not exist until the choices are made, so every step
   * ahead is still a draft and none of them can be revisited.
   */
  agent?: ManagedAgent;
  /** Leaves the wizard for the agent it created, or the list if it made none. */
  onDone: (agentID?: string) => void;
  onBusy?: (busy: boolean) => void;
  /**
   * How often the verify step re-reads the key. Only a test shortens it: at
   * the real cadence a test either waits five seconds per tick or races one.
   */
  verifyPollMs?: number;
}): JSX.Element {
  const sdk = useSdkClient();
  const organization = useOrganization();
  const project = useProject();
  const { user } = useSession();
  const queryClient = useQueryClient();
  const inventory = useServerInventory();
  // Trusting a platform and admitting a subject are both writes against the
  // organization's workload identities, so the choice is offered only to
  // someone who could carry it out.
  const {
    hasAllScopes,
    hasAnyScope,
    isLoading: rbacLoading,
    error: rbacError,
  } = useRBAC();
  const canFederate = hasAllScopes(["workload:write"]);
  // A new agent sends hook events from the observability plugin when its
  // creator may grant that. Only confirmed permissions count: a failed grants
  // read must not add a grant that would refuse the whole creation.
  const canGrantHookEvents =
    !rbacError &&
    hasAllScopes([HOOKS_INGEST_SCOPE], organization.id, project.id);
  // The catalog itself needs workload read access. Without it the request is
  // refused, so it is not made, and the step shows the API key alone rather
  // than an error. Write satisfies read on the server, so either scope counts.
  const catalog = useCatalogEntries(
    hasAnyScope(["workload:read", "workload:write"]),
  );
  const renameAgent = useRenameAgentMutation();
  // Only to keep a prefilled name from colliding, so a failure to read the
  // list leaves the field empty rather than blocking the flow.
  const agents = useAgents({}, undefined, { throwOnError: false });
  const existingNames = useMemo(
    () =>
      agents.data === undefined
        ? undefined
        : new Set(agents.data.result.items.map((item) => item.name)),
    [agents.data],
  );
  const createGrant = useAgentsCreatePolicyGrantMutation();
  const deleteGrant = useAgentsDeletePolicyGrantMutation();

  // Provisioning an agent that already exists: its name and scope are settled,
  // so the flow is only about which servers this key reaches and how the key
  // is delivered. Every step is a destination because none of them creates
  // anything until the key is issued.
  const existing = agent !== undefined;

  // Opened for a named platform: the credential is settled and the agent is
  // named after what will be signing in, so the flow starts at the servers —
  // the first thing still left to decide.
  const preset =
    initialPlatformKey === undefined
      ? undefined
      : catalog.entries.find((entry) => entry.key === initialPlatformKey);
  // The step lives in the URL so a step can be linked, reloaded and gone
  // back to. Replace rather than push: the browser's Back belongs to the
  // page the reader came from, and the wizard has its own Back.
  const [params, setParams] = useSearchParams();
  const defaultStep = initialPlatformKey === undefined ? 0 : 1;
  // Presence first: `Number(null)` and `Number("")` are both 0, which reads as
  // a deep link to the first step and leaves `defaultStep` unreachable.
  const linked = params.get("step");
  const fromUrl =
    linked === null || linked.trim() === "" ? Number.NaN : Number(linked);
  const step =
    Number.isInteger(fromUrl) && fromUrl >= 0 ? fromUrl : defaultStep;
  // Stable, so an effect that corrects the step can depend on it.
  const setStep = useCallback(
    (next: number) => {
      setParams(
        (current) => {
          const updated = new URLSearchParams(current);
          updated.set("step", String(next));
          return updated;
        },
        { replace: true },
      );
    },
    [setParams],
  );
  const [name, setName] = useState(agent?.name ?? "");
  // Names are unique in the organization, case aside. Asking once typing
  // pauses catches a taken name here instead of on Create agent, which still
  // refuses one another agent takes in between. A name the agent already has
  // is its own, and a failed check lets the server decide.
  const typedName = name.trim();
  const checksName =
    (!existing || editing) &&
    typedName !== "" &&
    typedName.toLowerCase() !== (agent?.name ?? "").trim().toLowerCase();
  const nameToCheck = useDebouncedValue(typedName, 400);
  const nameCheck = useQuery({
    queryKey: [
      "agent-name-available",
      organization.id,
      nameToCheck.toLowerCase(),
    ],
    // One page of the agents whose names contain it. A name in more than 200
    // of them that this page misses is left to the server to refuse.
    queryFn: async ({ signal }) => {
      const page = await sdk.agents.list(
        { search: nameToCheck, limit: 200 },
        undefined,
        { signal },
      );
      return !page.result.items.some(
        (match) =>
          match.id !== agent?.id &&
          match.name.trim().toLowerCase() === nameToCheck.toLowerCase(),
      );
    },
    enabled: checksName && nameToCheck === typedName,
    retry: false,
  });
  const nameState: "free" | "checking" | "taken" = !checksName
    ? "free"
    : // A cached answer is shown while it is asked again, and it may be out
      // of date by then.
      // A failed check answers only for the name it asked about.
      nameToCheck !== typedName || nameCheck.isPending || nameCheck.isFetching
      ? "checking"
      : nameCheck.isError
        ? "free"
        : nameCheck.data === false
          ? "taken"
          : "free";
  // null until the person picks, so the default can follow the project as it

  const [selected, setSelected] = useState<ServerSelection[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [provisioning, setProvisioning] = useState(false);
  const [saving, setSaving] = useState(false);
  // The credential the agent already holds, when one is being edited: an
  // agent that signs in with a platform's tokens must not open on "API key".
  const admitted = useAgentAdmissions(agent?.id ?? "").admissions[0];
  // An issuer whose catalog entry this wizard cannot set up, such as one not
  // offered yet, is edited as the custom issuer it would otherwise be.
  const admittedEntry =
    admitted === undefined
      ? undefined
      : catalogEntryForIssuer(
          catalog.entries.filter(hasPlatformSetup),
          admitted.issuer,
        );
  const [credential, setCredential] = useState<Credential>(
    initialPlatformKey === undefined ? "api-key" : "workload",
  );
  const [platformKey, setPlatformKey] = useState<string | null>(
    initialPlatformKey ?? null,
  );
  // Applied once the catalog and the admission have both arrived, and only
  // while the reader has not chosen something else.
  const adopted = useRef(false);
  useEffect(() => {
    if (adopted.current || !editing || admitted === undefined) return;
    // Both, not just the admission: the entry is looked up in the catalog, so
    // running before it arrives latches the custom key, and `adopted` then
    // stops the real platform from ever being recognised.
    if (catalog.isPending) return;
    adopted.current = true;
    setCredential("workload");
    setPlatformKey(admittedEntry?.key ?? CUSTOM_PLATFORM_KEY);
  }, [editing, admitted, admittedEntry, catalog.isPending]);
  // The catalog loads after the first render, so the preset name is filled
  // in when it arrives rather than in a state initializer that has nothing
  // to read yet. Only while the field is still untouched.
  const named = useRef(false);
  useEffect(() => {
    if (named.current || preset === undefined || name !== "") return;
    if (existingNames === undefined) return;
    named.current = true;
    setName(freeName(preset.displayName, existingNames));
  }, [preset, name, existingNames]);

  // The servers the agent already reaches, filled in once its grants and the
  // server list have both arrived, and only once: after that the selection
  // is the reader's.
  //
  // Only a server list that actually loaded counts. A failed read leaves the
  // selection empty, which is indistinguishable from "no servers chosen" —
  // and saving that against the stored grants withdraws every one of them, so
  // nothing may be saved until this has run.
  const [reconciled, setReconciled] = useState(false);
  // The grants as the save last knew them, so a retry after a partial save
  // writes what is still missing rather than creating a grant a second time.
  const applied = useRef(new Map<string, GrantedServer>());
  // Keep the policy originally read for every pending deletion, including
  // grants created by a partially completed save. A rejected delete keeps it.
  const appliedPolicies = useRef(new Map<string, AgentPolicyGrant>());
  // The wildcard as the save last knew it, for the same reason: `grants` is
  // the answer the query gave before the save started, so a retry reading it
  // would create a second wildcard or ask for an id that is already gone.
  const appliedWildcard = useRef<WildcardConnectGrant | undefined>(undefined);
  // Server grants this edit gave the agent that its keys do not hold yet. A
  // key stores the grants it was issued with, so without passing these on the
  // agent's gateway never lists the servers just added. Kept across a retry,
  // because the grants a failed save already wrote are not in the next diff.
  const keysMissing = useRef<ExtendAgentKeyGrantForm[]>([]);
  useEffect(() => {
    if (reconciled || grants === undefined) return;
    if (inventory.isLoading || inventory.isError) return;
    const granted = grantedServers(grants);
    applied.current = granted;
    appliedPolicies.current = new Map(grants.map((grant) => [grant.id, grant]));
    setSelected(
      inventory.servers
        .filter((server) => granted.has(server.resourceId))
        .map((server) => ({ server })),
    );
    const wildcard = wildcardConnectGrant(grants);
    appliedWildcard.current = wildcard;
    // The wildcard is the broader claim, so it wins the display: an agent
    // holding one reaches every server whatever else its policy lists.
    setAllServers(wildcard !== undefined);
    setReconciled(true);
  }, [
    grants,
    inventory.servers,
    inventory.isLoading,
    inventory.isError,
    reconciled,
  ]);
  // A wildcard grant reaches every server its selector covers and is
  // deliberately not a row in the list, so the reader has to be told it is
  // there — and how far it goes, because one scoped to a project does not
  // reach the organization's other projects.
  const wildcardReach =
    editing && grants !== undefined ? wildcardConnectGrant(grants) : undefined;
  // "Every server, including ones added later" as a choice the picker can
  // hold, rather than a state it can only describe. A wildcard grant is what
  // it saves to, so an agent that already has one opens with it on and the
  // list reads as what the policy actually allows.
  const [allServers, setAllServers] = useState(false);

  // What provisioning produced. The secret is held only while this page is
  // open; nothing writes it to storage or to the query cache.
  const [agentID, setAgentID] = useState<string | null>(agent?.id ?? null);
  // Whether the agent this flow created was given hook ingestion. Once it is
  // created its policy is settled, so that choice holds even if the caller's
  // permissions change afterwards. An agent that exists keeps the policy it
  // has, and its keys are not asked for hook ingestion here.
  const [createdHookEvents, setCreatedHookEvents] = useState<boolean | null>(
    null,
  );
  const hookEvents = !existing && (createdHookEvents ?? canGrantHookEvents);
  const [secret, setSecret] = useState<string | null>(null);
  const [command, setCommand] = useState<string | null>(null);
  const [commandError, setCommandError] = useState<string | null>(null);
  const [minting, setMinting] = useState(false);
  const [verify, setVerify] = useState<VerifyState>("waiting");
  const [firstCallAt, setFirstCallAt] = useState<Date | undefined>();
  const keyID = useRef<string | null>(null);
  // Minting a setup command authenticates with the key, so the key's own
  // access time moves before any runtime has called. Only an access after the
  // last thing this page did counts as the agent's first call.
  const lastSelfUse = useRef<number>(0);

  useEffect(() => {
    // Minting counts as busy too: the key exists by then, and leaving while
    // the setup command is still being prepared throws away the one chance
    // to copy a secret that is shown once. So does saving: it writes one
    // grant per request, and leaving part-way through stores half an edit.
    onBusy?.(provisioning || minting || saving);
  }, [provisioning, minting, saving, onBusy]);

  // Where the agent lives, not a question anyone is asked: a new agent
  // belongs to the project it is being created in. It was offered as a
  // choice, which was the same question the server list answers and a second
  // place for the two to disagree — `project_id` narrows nothing on its own,
  // and the grants below decide the whole of what the agent can reach.
  //
  // An existing agent's is its own and is stored on it: the project being
  // browsed is not necessarily the one it was bound to. Agents from before
  // the column existed hold NULL, which is "not yet scoped" rather than
  // "organization", so they keep being offered every project's servers.
  const scopedProjectID = existing ? agent.projectId : project.id || undefined;
  // Falls back to the id, not the browsed project: an agent bound to a project
  // the caller cannot see would otherwise be labelled with the wrong one.
  const projectName = (id: string): string =>
    (organization.projects ?? []).find((candidate) => candidate.id === id)
      ?.name ?? id;
  const scopeName = scopedProjectID
    ? projectName(scopedProjectID)
    : organization.name;
  // What "All servers" covers. An agent that already holds wildcards keeps
  // their own scope: re-issuing a key must not quietly narrow an
  // organization-wide grant down to the project the agent is labelled with,
  // nor drop the projects of every wildcard but the first. Empty is every
  // project. The reach is named on screen from the same value, so a wildcard
  // wider than its agent reads as what it is rather than as the agent's
  // project.
  const allServersProjectIDs = wildcardReach
    ? wildcardReach.projectIds
    : scopedProjectID
      ? [scopedProjectID]
      : [];
  const allServersScopeName =
    allServersProjectIDs.length === 0
      ? organization.name
      : allServersProjectIDs.map(projectName).join(", ");
  // The wildcard "All servers" would write, or undefined where choosing it
  // writes nothing: an agent that already holds one keeps it. A new agent's
  // waits for its project, as creating one does.
  const writesGrants = !existing || editing;
  const wildcardToWrite = useMemo(() => {
    if (!writesGrants) return undefined;
    if (editing && wildcardReach !== undefined) return undefined;
    const projectId = existing ? scopedProjectID : project.id;
    if (!existing && !projectId) return undefined;
    return {
      resourceKind: "mcp" as const,
      resourceId: "*",
      ...(projectId ? { projectId } : {}),
    };
  }, [
    writesGrants,
    existing,
    editing,
    wildcardReach,
    scopedProjectID,
    project.id,
  ]);
  // The servers a tick would grant: every one the list offers that the agent
  // does not reach yet. A grant it already holds is kept, never written again.
  const grantedIds = useMemo(
    () => new Set(grants === undefined ? [] : grantedServers(grants).keys()),
    [grants],
  );
  const serversToCheck = useMemo(
    () =>
      !writesGrants || (!existing && !project.id)
        ? []
        : inventory.servers.filter(
            (server) =>
              (!server.issuerId || (!editing && !existing)) &&
              !grantedIds.has(server.resourceId) &&
              (scopedProjectID === undefined ||
                server.projectId === scopedProjectID),
          ),
    [
      writesGrants,
      existing,
      editing,
      project.id,
      inventory.servers,
      grantedIds,
      scopedProjectID,
    ],
  );
  // A grant is accepted only when the person saving it, and the agent's
  // owner, can reach what it covers: for "All servers" that includes servers
  // this list never shows them. The server answers with the rule the write
  // itself applies, so a choice it would refuse is withheld up front rather
  // than offered and then refused when the agent is created.
  const checkedSelectors = useMemo(
    () => [
      ...(wildcardToWrite ? [wildcardToWrite] : []),
      ...serversToCheck.map((server) => ({
        resourceKind: "mcp" as const,
        resourceId: server.resourceId,
        projectId: server.projectId,
      })),
    ],
    [wildcardToWrite, serversToCheck],
  );
  const agentId = agent?.id;
  const policyCheck = useQuery({
    queryKey: [
      "agent-policy-check",
      organization.id,
      user.id,
      agentId ?? null,
      checkedSelectors,
    ],
    queryFn: async ({ signal }) => {
      const answers: AgentPolicyGrantCheck[] = [];
      for (let at = 0; at < checkedSelectors.length; at += CHECK_BATCH_SIZE) {
        const { results } = await sdk.agents.checkPolicyGrants(
          {
            checkAgentPolicyGrantsForm: {
              ...(agentId ? { agentId } : {}),
              policyGrants: checkedSelectors
                .slice(at, at + CHECK_BATCH_SIZE)
                .map((selector) => ({
                  effect: "allow",
                  scope: CONNECT_SCOPE,
                  selector,
                })),
            },
          },
          undefined,
          { signal },
        );
        answers.push(...results);
      }
      return answers;
    },
    enabled: !inventory.isLoading && checkedSelectors.length > 0,
    retry: false,
    throwOnError: false,
  });
  const refetchPolicyCheck = policyCheck.refetch;
  const recheck = useCallback(() => {
    if (checkedSelectors.length > 0) void refetchPolicyCheck();
  }, [checkedSelectors, refetchPolicyCheck]);
  // A failed refetch retains React Query's last answer, which no longer
  // establishes that the selection can be saved.
  const checkedAnswers = policyCheck.isError ? undefined : policyCheck.data;
  const wildcardAnswer = wildcardToWrite ? checkedAnswers?.[0] : undefined;
  const allServersRefusal = ((): AllServersRefusal | undefined => {
    if (wildcardToWrite === undefined) return undefined;
    if (wildcardAnswer?.allowed === true) return undefined;
    if (wildcardAnswer?.limitedBy === "owner") {
      return {
        message: `This agent's owner cannot reach every server in ${allServersScopeName}, so the agent cannot either. Choose its servers below, or ask an administrator to review the owner's access.`,
      };
    }
    if (wildcardAnswer !== undefined) {
      return {
        message: `You cannot reach every server in ${allServersScopeName}, so this agent cannot either. Choose its servers below, or ask an administrator for access to the rest.`,
      };
    }
    if (policyCheck.isError) {
      return {
        message: `Could not check that you can reach every server in ${allServersScopeName}.`,
      };
    }
    return {
      message: `Checking that you can reach every server in ${allServersScopeName}…`,
      pending: true,
    };
  })();
  // The servers a tick would be refused for, by id, with why.
  const refusedServers = useMemo(() => {
    const refused = new Map<string, string>();
    const offset = wildcardToWrite ? 1 : 0;
    serversToCheck.forEach((server, index) => {
      const answer = checkedAnswers?.[offset + index];
      if (answer === undefined || answer.allowed) return;
      refused.set(
        server.id,
        answer.limitedBy === "owner"
          ? "This agent's owner cannot reach this server, so the agent cannot either."
          : "You cannot reach this server, so this agent cannot either.",
      );
    });
    return refused;
  }, [wildcardToWrite, serversToCheck, checkedAnswers]);
  // A choice is saved only once the check has accepted it: one it refused
  // would be refused by the server in turn, and one it has not answered for,
  // still pending or failed, might be. "All servers" can be on while its
  // answer changes, and a server can be ticked before its answer arrives.
  // Said here, before anything is written, instead.
  const checkedIds = useMemo(
    () => new Set(serversToCheck.map((server) => server.id)),
    [serversToCheck],
  );
  const refusedSelection = allServers
    ? []
    : selected.filter((entry) => refusedServers.has(entry.server.id));
  const unansweredSelection =
    allServers || checkedAnswers !== undefined
      ? []
      : selected.filter((entry) => checkedIds.has(entry.server.id));
  const refusedChoice =
    allServers && allServersRefusal !== undefined
      ? allServersRefusal.message
      : refusedSelection.length > 0
        ? `Deselect ${refusedSelection.map((entry) => entry.server.name).join(", ")}: ${refusedServers.get(refusedSelection[0]!.server.id)}`
        : unansweredSelection.length > 0
          ? policyCheck.isError
            ? "Could not check that you can reach the servers you chose. Check again above the list."
            : "Checking that you can reach the servers you chose…"
          : null;
  // The blanket sign-in grant is written only for an agent that reaches a
  // server: one that reaches nothing has no auth client to use. A new agent
  // gets it when created, an edited one when it holds no mcp:auth yet.
  // Whether the person saving may give it is asked as soon as the first
  // server is chosen, so a refusal stops the flow on the server step.
  const reachesServers = allServers || selected.length > 0;
  const writesAuthGrant =
    reachesServers &&
    (editing
      ? (grants ?? []).every((grant) => grant.scope !== AUTH_GRANT.scope)
      : !existing);
  const enforcement = useUpstreamAuthEnforcement();
  const authCheck = useQuery({
    queryKey: ["agent-auth-check", organization.id, user.id, agentId ?? null],
    queryFn: async ({ signal }) => {
      const { results } = await sdk.agents.checkPolicyGrants(
        {
          checkAgentPolicyGrantsForm: {
            ...(agentId ? { agentId } : {}),
            policyGrants: [AUTH_GRANT],
          },
        },
        undefined,
        { signal },
      );
      return results[0];
    },
    enabled: writesAuthGrant,
    retry: false,
    throwOnError: false,
  });
  const authRefused = writesAuthGrant && authCheck.data?.allowed === false;
  // An edit writes grants one by one, so saving while the check is still out
  // could write the servers and then be refused the sign-in grant. Creating
  // sends every grant in one request, which the server accepts or refuses
  // whole, so it does not wait.
  const authPending = editing && writesAuthGrant && authCheck.isLoading;
  const authPendingNote = `Checking that you can give this agent ${AUTH_GRANT.scope}…`;
  // Where the organization enforces mcp:auth, an agent without it cannot sign
  // in to any upstream, so a refused grant stops the flow before anything is
  // written. Elsewhere sign-in does not need it yet, so the grant is left out
  // instead of failing the save. An unknown answer keeps the grant, and the
  // server decides.
  const authStop = !authRefused
    ? null
    : enforcement === "loading"
      ? "Checking whether this organization requires sign-in permission…"
      : enforcement !== "enforced"
        ? null
        : authCheck.data?.limitedBy === "owner"
          ? `This agent's owner cannot give it ${AUTH_GRANT.scope}, which this organization requires to sign in to any server. Ask an administrator to review the owner's access.`
          : `You cannot give this agent ${AUTH_GRANT.scope}, which this organization requires to sign in to any server. Ask an administrator for access.`;
  const includeAuthGrant = !(authRefused && enforcement === "legacy");
  // The rows the picker offers. Under "All servers" they are the ones those
  // wildcards reach, so an organization-wide one shows every project's;
  // otherwise it is the project the agent is bound to.
  const listedServers = inventory.servers.filter((server) =>
    allServers
      ? reachCoversProject(allServersProjectIDs, server.projectId)
      : scopedProjectID === undefined || server.projectId === scopedProjectID,
  );
  // New agents attach the creator's connections as part of provisioning.
  const offersConnections = !editing && !existing;
  const connectionServers = [
    ...new Map(
      [...listedServers, ...selected.map((entry) => entry.server)].map(
        (server) => [server.id, server],
      ),
    ).values(),
  ];
  const connections = useOwnerConnections(connectionServers, true, agent?.id);

  const gatewayURL = agentID ? agentGatewayURL(agentID) : "";
  const platform = catalog.entries.find((entry) => entry.key === platformKey);
  // What the platform's runtime needs beyond the servers, such as a device
  // agent's polling and hook events. Empty for an API key and for platforms
  // that only call MCP.
  const platformGrants = useMemo<AgentPolicyGrantForm[]>(
    () =>
      credential === "workload" && platform !== undefined && project.id
        ? (platformSetup(platform)?.agentGrants?.(
            organization.id,
            project.id,
          ) ?? [])
        : [],
    [credential, platform, organization.id, project.id],
  );
  // The platform grants the agent still lacks, each checked by the rule
  // agents.create and agents.createPolicyGrant apply, so a grant the caller or
  // the owner could not give is named before anything is written. A new agent
  // lacks them all; one that exists is asked only for what it does not hold.
  const platformGrantCheck = useQuery({
    queryKey: [
      "agent-platform-grant-check",
      organization.id,
      user.id,
      agent?.id ?? null,
      platformGrants,
    ],
    queryFn: async ({ signal }) => {
      const held = agent
        ? await sdk.agents.listPolicyGrants({ agentId: agent.id }, undefined, {
            signal,
          })
        : [];
      const missing = platformGrants.filter(
        (grant) => !held.some((existing) => grantCovers(existing, grant)),
      );
      if (missing.length === 0) return { missing, refusal: null };
      const { results } = await sdk.agents.checkPolicyGrants(
        {
          checkAgentPolicyGrantsForm: {
            ...(agent ? { agentId: agent.id } : {}),
            policyGrants: missing,
          },
        },
        undefined,
        { signal },
      );
      return { missing, refusal: grantRefusal(missing, results) };
    },
    enabled: platformGrants.length > 0 && !editing,
    retry: false,
    throwOnError: false,
  });
  /**
   * The platform grants to write, once every one has been checked; throws
   * with the reason when one would be refused or the check could not run.
   */
  const checkedPlatformGrants = async (): Promise<AgentPolicyGrantForm[]> => {
    if (platformGrants.length === 0 || editing) return [];
    // Asked again rather than read from the cache: the answer shown while the
    // wizard was open may predate a grant written or withdrawn since, and a
    // stale list of what is missing is the partial write this check prevents.
    // A failed refetch keeps the last data, so the error is checked first.
    const result = await platformGrantCheck.refetch();
    const answer = result.isError ? undefined : result.data;
    if (answer === undefined) {
      throw new Error(
        "Could not check that this agent can be given the permissions its platform needs. Try again.",
      );
    }
    if (answer.refusal !== null) throw new Error(answer.refusal);
    return answer.missing;
  };

  // The verify step reads the key itself: a key with an access time is a
  // runtime that reached the gateway and was admitted.
  useEffect(() => {
    if (step !== 5 || !agentID || verify === "connected") return;
    let cancelled = false;
    const poll = async () => {
      try {
        const listed = await sdk.keys.list({ agentId: agentID });
        const issued = listed.keys.find((key) => key.id === keyID.current);
        if (
          !cancelled &&
          issued?.lastAccessedAt &&
          issued.lastAccessedAt.getTime() > lastSelfUse.current
        ) {
          setVerify("connected");
          setFirstCallAt(issued.lastAccessedAt);
        }
      } catch {
        // A failed poll says nothing about the agent; the next one retries.
      }
    };
    void poll();
    const timer = window.setInterval(() => void poll(), verifyPollMs);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [step, agentID, verify, sdk, verifyPollMs]);

  const regenerate = (key: string, url: string) => {
    // Minting authenticates with the key, so a plaintext endpoint would hand
    // it to anyone on the path. Loopback is exempt: it never reaches a
    // network. The same rule withholds the instructions on the step itself.
    if (!isCredentialSafe(url)) {
      setCommandError(
        `This deployment serves MCP over plaintext HTTP at ${url}, so a setup command is not prepared: it would send the agent key unencrypted. Configure an HTTPS server URL and reload.`,
      );
      return;
    }
    setMinting(true);
    setCommandError(null);
    lastSelfUse.current = Date.now();
    mintInstallCommand(url, key)
      .then((next) => {
        lastSelfUse.current = Date.now();
        setCommand(next);
      })
      .catch((failure: Error) => setCommandError(failure.message))
      .finally(() => setMinting(false));
  };

  /**
   * The agent this flow provisions, created on first use. Both credentials
   * need it and neither creates it until the choices before it are made, so
   * it is created once here and reused — a retry after a later failure must
   * not leave a second agent behind.
   */
  const ensureAgent = async (): Promise<string> => {
    // An agent that exists keeps its stored ceiling: this flow provisions
    // against it rather than rewriting what it may ever be delegated.
    if (agent) return agent.id;
    if (agentID) return agentID;
    // A new agent belongs to the project it is created in, so there is
    // nothing to create until that project has resolved. An empty id is a
    // context still loading, not an organization-wide agent: creating on it
    // would leave the agent bound to no project and, under "All servers", a
    // wildcard with no project on it — the whole organization, by accident.
    const projectID = project.id;
    if (!projectID) {
      throw new Error("This project is still loading. Try again in a moment.");
    }
    if (authStop) throw new Error(authStop);
    if (refusedChoice) throw new Error(refusedChoice);
    // Whether the agent sends hook events is read from the caller's grants,
    // so it is not decided while they are still loading.
    if (rbacLoading) {
      throw new Error(
        "Your permissions are still loading. Try again in a moment.",
      );
    }
    // "All servers" is one wildcard selector rather than today's inventory
    // listed out, so it covers the servers added after this is saved. Scoped
    // to the project, never the draft's bare `null`: `null` means
    // unrestricted and produces a wildcard with no project on it, which would
    // reach every project while the agent itself belongs to one.
    const draft: AgentPolicyDraft = {
      "mcp:connect": allServers
        ? [
            {
              resourceKind: "mcp" as const,
              resourceId: "*",
              projectId: projectID,
            },
          ]
        : selected.map((entry) => ({
            resourceKind: "mcp" as const,
            resourceId: entry.server.resourceId,
            projectId: entry.server.projectId,
          })),
    };
    const created = await sdk.agents.create({
      createAgentForm: {
        name: name.trim(),
        projectId: projectID,
        // Hook ingestion is bounded to the agent's project, the only one its
        // hook events may name. A platform's own grants may repeat it, and
        // the server refuses a request that names a grant twice.
        policyGrants: uniqueGrants([
          ...agentPolicyGrantsFromDraft(draft),
          ...(includeAuthGrant && reachesServers ? [AUTH_GRANT] : []),
          ...(hookEvents
            ? [hooksIngestPolicyGrant(organization.id, projectID)]
            : []),
          ...platformGrants,
        ]),
      },
    });
    setAgentID(created.id);
    setCreatedHookEvents(hookEvents);
    void invalidateAgentPolicy(
      queryClient,
      organization.id,
      user.id,
      created.id,
    );
    void queryClient.invalidateQueries({
      queryKey: ["managed-agents", organization.id],
    });
    return created.id;
  };

  /**
   * Gives an agent that already exists the platform grants it lacks, already
   * checked by checkedPlatformGrants: each is its own write, so all are
   * checked before the first is made.
   */
  const addPlatformGrants = async (
    agentId: string,
    missing: AgentPolicyGrantForm[],
  ): Promise<void> => {
    if (missing.length === 0) return;
    for (const grant of missing) {
      await createGrant.mutateAsync({
        request: {
          createAgentPolicyGrantForm: {
            agentId,
            effect: "allow",
            scope: grant.scope,
            selector: grant.selector,
          },
        },
      });
    }
    void invalidateAgentPolicy(queryClient, organization.id, user.id, agentId);
  };

  /**
   * Write an edit back: the name if it changed, and the difference between
   * the servers the agent reaches and the ones now chosen.
   *
   * A diff rather than a rewrite, because each grant is a row with its own
   * id and its own line in the audit log: replacing all of them would churn
   * every one of those on a change to a single server.
   */
  const save = async (): Promise<void> => {
    if (agent === undefined || grants === undefined) return;
    // The selection is only the agent's reach once the grants have been
    // reconciled into it; before that every grant would read as withdrawn.
    if (!reconciled || saving || authPending) return;
    if (authStop) {
      setError(authStop);
      return;
    }
    if (refusedChoice) {
      setError(refusedChoice);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (name.trim() !== agent.name) {
        await renameAgent.mutateAsync({
          request: { renameAgentForm: { id: agent.id, name: name.trim() } },
        });
      }
      // Only the grants whose server this list offers: one naming a server the
      // reader was never shown cannot have been unchecked, so an absence from
      // the selection does not mean they gave it up.
      const offered = new Set(
        inventory.servers.map((server) => server.resourceId),
      );
      const known = new Map(
        [...applied.current].filter(([resourceId]) => offered.has(resourceId)),
      );
      // Under "All servers" the per-server rows say nothing the wildcard does
      // not already say, so the chosen list is empty and every named grant it
      // replaces is withdrawn. Otherwise it is the ticked list as it stands.
      const changes = grantChanges(
        known,
        allServers
          ? []
          : selected.map((entry) => ({
              resourceId: entry.server.resourceId,
              projectId: entry.server.projectId,
            })),
      );
      // The wildcard goes in before the named grants it supersedes come out,
      // so no moment of this save leaves the agent reaching less than both the
      // old policy and the new one allow.
      if (allServers && appliedWildcard.current === undefined) {
        const form = {
          effect: "allow" as const,
          scope: "mcp:connect" as const,
          selector: {
            resourceKind: "mcp" as const,
            resourceId: "*",
            // Scoped where the agent is, so "all servers" means all the
            // ones this agent could ever be pointed at, not the whole
            // organization's.
            ...(scopedProjectID ? { projectId: scopedProjectID } : {}),
          },
        };
        const created = await createGrant.mutateAsync({
          request: {
            createAgentPolicyGrantForm: { agentId: agent.id, ...form },
          },
        });
        // Recorded as the write lands, so a failure further down this save
        // does not make the next attempt create a second wildcard.
        appliedPolicies.current.set(created.id, created);
        keysMissing.current.push(form);
        appliedWildcard.current = {
          projectIds: scopedProjectID ? [scopedProjectID] : [],
          grantIds: [created.id],
        };
      }
      for (const server of changes.add) {
        const form = {
          effect: "allow" as const,
          scope: "mcp:connect" as const,
          selector: {
            resourceKind: "mcp" as const,
            resourceId: server.resourceId,
            projectId: server.projectId,
          },
        };
        const created = await createGrant.mutateAsync({
          request: {
            createAgentPolicyGrantForm: { agentId: agent.id, ...form },
          },
        });
        // Recorded as each write lands, so a later failure does not send the
        // whole diff again on the next attempt.
        appliedPolicies.current.set(created.id, created);
        keysMissing.current.push(form);
        applied.current.set(server.resourceId, {
          grantIds: [created.id],
          resourceId: server.resourceId,
          projectId: server.projectId,
        });
      }
      if (
        includeAuthGrant &&
        reachesServers &&
        ![...appliedPolicies.current.values()].some(
          (grant) => grant.scope === AUTH_GRANT.scope,
        )
      ) {
        const created = await createGrant.mutateAsync({
          request: {
            createAgentPolicyGrantForm: { agentId: agent.id, ...AUTH_GRANT },
          },
        });
        appliedPolicies.current.set(created.id, created);
      }
      // Once the agent holds every grant its keys are given, and before the
      // removals, so a removal that fails cannot keep the new servers from the
      // keys. Removals need nothing here: a key only ever reaches what the
      // agent still allows. The grants are saved by now, so a key that could
      // not follow is reported rather than undoing them.
      //
      // Only what this save still chooses: a server added by an earlier
      // attempt and unticked since is no longer the agent's to pass on.
      keysMissing.current = keysMissing.current.filter((form) =>
        form.selector.resourceId === "*"
          ? allServers
          : !allServers &&
            selected.some(
              (entry) => entry.server.resourceId === form.selector.resourceId,
            ),
      );
      if (keysMissing.current.length > 0) {
        try {
          // The credential picked on MCP sign-in for each added server, as a
          // key issued now would carry it.
          const choices = await keyCredentialChoices(
            sdk,
            selected
              .filter((entry) =>
                keysMissing.current.some(
                  (form) =>
                    form.selector.resourceId === entry.server.resourceId,
                ),
              )
              .map((entry) => ({
                ...entry.server,
                authClientId: entry.authClientId,
              })),
          );
          for (
            let start = 0;
            start < keysMissing.current.length;
            start += EXTEND_KEYS_BATCH
          ) {
            const batch = keysMissing.current.slice(
              start,
              start + EXTEND_KEYS_BATCH,
            );
            await sdk.keys.extendAgentKeys({
              extendAgentKeysForm: {
                agentId: agent.id,
                requestedGrants: batch,
                authClientChoices: choices.filter((choice) =>
                  batch.some(
                    (form) => form.selector.resourceId === choice.mcpServerId,
                  ),
                ),
              },
            });
          }
          keysMissing.current = [];
          void invalidateAllListAPIKeys(queryClient);
        } catch (failure) {
          toast.warning(
            `Saved, but the agent's existing keys still reach only their old servers: ${failure instanceof Error ? failure.message : String(failure)}. Issue a new key from the agent's page to use the new ones.`,
          );
        }
      }
      // Last, so a failure part-way through leaves the agent reaching more
      // than intended rather than less: the servers it is meant to keep are
      // already granted by the time the wildcard goes.
      const removals = allServers
        ? changes.remove
        : [...changes.remove, ...(appliedWildcard.current?.grantIds ?? [])];
      for (const grantId of removals) {
        const baseline = appliedPolicies.current.get(grantId);
        if (!baseline)
          throw new Error(
            "The original permission could not be found. Reopen the editor before saving.",
          );
        await deleteGrant.mutateAsync({
          request: {
            deleteAgentPolicyGrantForm: {
              agentId: agent.id,
              grantId,
              expectedPolicy: {
                scope: baseline.scope,
                selector: baseline.selector,
              },
            },
          },
        });
        appliedPolicies.current.delete(grantId);
        for (const [resourceId, server] of applied.current) {
          const left = server.grantIds.filter((id) => id !== grantId);
          // A server can be reached through several grants, so it is only off
          // the list once the last of them is gone.
          if (left.length === 0) applied.current.delete(resourceId);
          else applied.current.set(resourceId, { ...server, grantIds: left });
        }
        // Same for the wildcard rows: a retry after a later failure asks for
        // the ones still standing rather than for an id already deleted.
        const wildcard = appliedWildcard.current;
        if (wildcard !== undefined) {
          const left = wildcard.grantIds.filter((id) => id !== grantId);
          appliedWildcard.current =
            left.length === 0 ? undefined : { ...wildcard, grantIds: left };
        }
      }
      // The agent's own page reads its servers from these, and it is where
      // saving leads.
      void invalidateAgentPolicy(
        queryClient,
        organization.id,
        user.id,
        agent.id,
      );
      // As when creating: the editor's usable connection is assigned to every
      // server that needs one. Valid assignments, someone else's included,
      // are kept; only ended or unavailable ones are replaced.
      const missing = await attachOwnerConnections(
        sdk,
        agent.id,
        allServers ? listedServers : selected.map((entry) => entry.server),
        undefined,
        true,
      );
      if (missing.length)
        toast.warning(
          `No connection assigned for: ${missing.join(", ")}. Connect these servers to finish setup.`,
        );
      await queryClient.invalidateQueries({
        queryKey: ["owner-connections"],
      });
      // Saving is the end of editing: what follows are the platform's own
      // settings, which this flow only describes. It returns to the agent's
      // own page, where the change shows.
      onDone(agent.id);
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : "Could not save this agent. Try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  // What the reader last decided, marked on the rail. The rail is a growing
  // block of text, and without this it looks the same before and after a
  // click: the mark is how a glance confirms the choice landed.
  const [highlight, setHighlight] = useState<string | null>(null);

  const connectionReach = (): InventoryServer[] =>
    allServers
      ? inventory.servers.filter((server) =>
          reachCoversProject(allServersProjectIDs, server.projectId),
        )
      : selected.map((entry) => entry.server);

  const attachSelectedConnections = async (
    targetId: string,
    reach: InventoryServer[],
    signal?: AbortSignal,
  ): Promise<void> => {
    if (offersConnections) {
      try {
        const missing = await attachOwnerConnections(
          sdk,
          targetId,
          reach,
          signal,
          allServers,
        );
        if (missing.length)
          toast.warning(
            `No connection assigned for: ${missing.join(", ")}. Connect these accounts to let the agent use them.`,
          );
      } catch (failure) {
        const reason = failure instanceof Error ? failure.message : "";
        throw new Error(
          `The agent was created, but it could not use your connection. ${reason}`.trim(),
        );
      }
    }
  };

  const workload = useWorkloadSetup({
    entry: credential === "workload" ? platform : undefined,
    platformKey: credential === "workload" ? platformKey : null,
    agentName: name.trim(),
    servers: connectionReach().flatMap((server) =>
      server.mcpUrl
        ? [
            {
              id: server.id,
              resourceId: server.resourceId,
              name: server.name,
              mcpUrl: server.mcpUrl,
            },
          ]
        : [],
    ),
    ensureAgent: async () => {
      // Editing an agent's rule leaves its policy as it is; otherwise every
      // grant the platform needs is checked before anything is written.
      const missing = await checkedPlatformGrants();
      const targetId = await ensureAgent();
      // A new agent was created with them; one that exists gets what it lacks.
      if (agent !== undefined) await addPlatformGrants(agent.id, missing);
      await attachSelectedConnections(targetId, connectionReach());
      return targetId;
    },
    onConnected: setAgentID,
    // The rule this agent already holds. Editing its values replaces it.
    replacing: editing ? admitted : undefined,
    // Editing without permission to manage workload identities shows the
    // rule without letting it be changed.
    readOnly: editing && !canFederate,
    // What the rule already says, so editing shows it rather than an empty
    // field that would write a second rule.
    initialValues:
      admitted === undefined
        ? undefined
        : admittedEntry !== undefined
          ? {
              ...valuesFromIssuer(admittedEntry, admitted.issuer),
              ...valuesFromSubject(admittedEntry, admitted),
            }
          : // A custom issuer: the issuer picked from the trusted list, and
            // the subject as typed, without the `*` the match kind adds.
            {
              issuer_id: admitted.workloadIssuerId,
              subject:
                admitted.matchKind === "wildcard"
                  ? admitted.subject.replace(/\*$/, "")
                  : admitted.subject,
              match_kind: admitted.matchKind,
            },
    onValueChange: (key) =>
      setHighlight(SUMMARY_ROW_FOR_FIELD.get(key) ?? null),
  });
  // A platform's own setup steps are steps of this wizard, not a wizard of
  // their own: one stepper, one Back and one Continue throughout.
  const stepNames =
    credential === "workload" && workload.steps.length > 0
      ? [
          ...STEPS.slice(0, 4),
          ...workload.steps.map((entry) => ({
            label: entry.title,
            short: entry.short,
          })),
        ]
      : STEPS;
  /** The platform setup step on screen, when one is. */
  const workloadStep =
    credential === "workload" ? workload.steps[step - 4] : undefined;
  // How many steps there are is only known here, once the platform's own have
  // been counted, so an out-of-range link is corrected rather than refused:
  // the URL is rewritten to the last real step instead of leaving the wizard
  // on a step that does not exist.
  const lastStep = stepNames.length - 1;
  // A new agent's draft lives only in this page's memory, so a reload past
  // its first step has nothing to resume: the name, the servers and the key
  // are gone, and a later step would act on an empty draft — issuing a key
  // there tried to create an agent with no name. Start the draft over. One
  // effect with the clamp below, so the two cannot both rewrite the step.
  const restarted = useRef(false);
  useEffect(() => {
    const first = !restarted.current;
    restarted.current = true;
    if (first && !existing && step > defaultStep) {
      setStep(defaultStep);
      return;
    }
    if (step <= lastStep) return;
    setStep(lastStep);
  }, [existing, step, defaultStep, lastStep, setStep]);

  /**
   * Create the agent, discover what it may delegate, issue its first key, and
   * mint the setup command. Each step depends on the one before it, so a
   * failure stops here and says which part did not happen.
   */
  const provision = async () => {
    if (provisioning) return;
    setProvisioning(true);
    setError(null);
    try {
      const target = { id: await ensureAgent() };

      const controller = new AbortController();
      // A key reaches only what it stores, whatever the agent's policy says
      // later. So "All servers" asks for the agent's wildcard itself, with the
      // exclusions that narrow it, and the key reaches servers added after it
      // is issued.
      const wildcard = allServers
        ? await discoverWildcardKeyGrants(
            sdk.agents,
            target.id,
            allServersProjectIDs,
            controller.signal,
          )
        : [];
      // Otherwise the key names servers one by one: the ones selected, or,
      // when the wildcard cannot be delegated (an owner granted servers one
      // by one), every server in the agent's reach today. That key does not
      // reach servers added later; issuing a new key picks them up.
      //
      // Without this the key asks for nothing: `selected` is empty under
      // "all servers", and discovery rightly answers that nothing can be
      // delegated.
      const reach = connectionReach();
      await attachSelectedConnections(target.id, reach, controller.signal);
      const listed = wildcard.length > 0 ? [] : reach;
      const delegable =
        wildcard.length > 0
          ? wildcard
          : await discoverKeyServerGrants(
              sdk.agents,
              target.id,
              listed.map((server) => ({
                resourceId: server.resourceId,
                projectId: server.projectId,
                kind: server.kind,
              })),
              controller.signal,
            );
      const requestedGrants = buildRequestedGrants(
        delegable.map((grant) => ({ grant, narrowing: {} })),
      );
      // Discovery answers per server, so it can come back covering only some
      // of them. A key issued on that answer reaches the servers it covers
      // and silently fails on the rest, while every selected server is still
      // listed as provisioned — so none of it is issued until the gap is
      // named.
      const reachable = new Set(
        delegable.map(
          (grant) =>
            `${grant.selector.projectId ?? ""}:${grant.selector.resourceId}`,
        ),
      );
      const excluded = listed.filter(
        (server) => !reachable.has(`${server.projectId}:${server.resourceId}`),
      );
      if (excluded.length > 0 || requestedGrants.length === 0) {
        // A key is the overlap of three permissions: the agent's, its
        // owner's, and the caller's. The owner's are what a reader never
        // thinks to check — the agent's own page looks right up until the
        // moment its owner loses the server.
        //
        // The caller is almost always the owner, so naming all three as
        // equals would send people looking for a difference that is not
        // there. It is said last, and only as the case where it applies.
        const because =
          "A key cannot give more access than the agent's owner has, so check the agent's permissions and that its owner can still reach them." +
          (agent && agent.ownerUserId !== user.id
            ? " You do not own this agent, so it is limited by your own access too."
            : "");
        throw new Error(
          excluded.length === listed.length
            ? existing
              ? `This key would reach none of these servers. ${because}`
              : `The agent was created, but a key for it would reach none of these servers. ${because}`
            : `This key would not reach ${excluded.map((server) => server.name).join(", ")}. ${because} Deselect ${excluded.length === 1 ? "that server" : "those servers"} to issue a key for the rest.`,
        );
      }

      const authClientChoices = await keyCredentialChoices(
        sdk,
        allServers
          ? []
          : selected.map((entry) => ({
              ...entry.server,
              authClientId: entry.authClientId,
            })),
      );
      // Hook ingestion names the organization rather than a server, so only
      // discovery that names no server returns it. Asked for only when the
      // agent was created with it, and bounded to the agent's project.
      const hookGrants =
        hookEvents && scopedProjectID
          ? hooksIngestKeyGrants(
              await discoverUnscopedGrants(
                sdk.agents,
                target.id,
                controller.signal,
              ),
              scopedProjectID,
            )
          : [];

      const expiresAt = new Date(Date.now() + KEY_LIFETIME_DAYS * 86_400_000);
      const issued = await sdk.keys.create({
        createKeyForm: {
          agentId: target.id,
          // Unique per issue, not per day: live key names are unique across
          // the organization, so a date alone makes the second key of the day
          // collide and the issue fail with a 409.
          name: `${name.trim()} key ${new Date().toISOString().replace(/[:.]/g, "-")}`,
          expiresAt,
          // A carried exclusion is a blocklist scope, which a key can store
          // from version 4.
          delegatedGrantsVersion: wildcard.length > 0 ? 4 : 2,
          requestedGrants: [...requestedGrants, ...hookGrants],
          scopes: [],
          authClientChoices,
        },
      });
      keyID.current = issued.id;
      setSecret(issued.key ?? null);
      setStep(4);
      if (issued.key) regenerate(issued.key, agentGatewayURL(target.id));
    } catch (failure) {
      const conflict =
        failure instanceof GramError && failure.statusCode === 409
          ? failure.message
          : null;
      setError(
        conflict ??
          (failure instanceof Error
            ? failure.message
            : "Could not set this agent up. Try again."),
      );
    } finally {
      setProvisioning(false);
    }
  };

  // A platform's own grants can add hook events, and a device agent's plugin
  // sync, to what the agent can do.
  const recordsSessions =
    hookEvents ||
    platformGrants.some((grant) => grant.scope === HOOKS_INGEST_SCOPE);
  const fetchesPlugins = platformGrants.some(
    (grant) => grant.scope === DEVICE_AGENT_SYNC_SCOPE,
  );
  const summary = (
    <WizardSummary
      step={step + 1}
      stepCount={stepNames.length}
      name={name}
      rows={[
        {
          label: "Owner",
          value:
            agent && agent.ownerUserId !== user.id
              ? agent.ownerProfile?.displayName || "Agent owner"
              : user.displayName || user.email,
        },
        {
          label: "Project",
          value: scopeName,
        },
        // What creating grants, in words. Editing leaves the hooks grant as it
        // is and does not read it, so the row would only guess. Before any
        // server is chosen there is nothing to call yet.
        ...(existing || (!allServers && selected.length === 0)
          ? []
          : [
              {
                label: "Can",
                value: (
                  <GrantedScopes
                    scopes={[
                      ...new Set([
                        allServers
                          ? `${CONNECT_SCOPE} on all servers in the project`
                          : `${CONNECT_SCOPE} on the selected servers`,
                        ...(includeAuthGrant
                          ? [`${AUTH_GRANT.scope} on all auth clients`]
                          : []),
                        ...(recordsSessions ? [HOOKS_INGEST_SCOPE] : []),
                        // What the platform's runtime needs beyond the
                        // servers, such as a device agent's plugin sync.
                        ...platformGrants.map((grant) => grant.scope),
                      ]),
                    ]}
                  >
                    {[
                      "Call its servers",
                      ...(fetchesPlugins ? ["fetch its plugins"] : []),
                      ...(recordsSessions ? ["record its sessions"] : []),
                    ].join(", ")}
                  </GrantedScopes>
                ),
              },
            ]),
        {
          label: "Agent sign-in",
          value: credential === "api-key" ? "API key" : "Workload identity",
        },
        // The platform, and what it was pinned to, are decisions like the
        // others: they belong on the rail rather than only on the step that
        // asked for them, which is two screens back by the time they matter.
        ...(credential === "workload" && platformKey !== null
          ? [
              {
                label: "Platform",
                value:
                  platformKey === CUSTOM_PLATFORM_KEY
                    ? (workload.values["name"]?.trim() ?? "") || "Custom"
                    : (platform?.displayName ?? "Custom"),
              },
            ]
          : []),
        ...(credential === "workload" &&
        (workload.values["org_id"] ?? "").trim() !== ""
          ? [
              {
                label: "Anthropic organization",
                value: (
                  <code className="text-xs">
                    {workload.values["org_id"]?.trim()}
                  </code>
                ),
              },
            ]
          : []),
        ...(credential === "workload" &&
        platformKey === GITHUB_ACTIONS_KEY &&
        (workload.values["owner"] ?? "").trim() !== "" &&
        (workload.values["repo"] ?? "").trim() !== ""
          ? [
              {
                label: "Repository",
                value: (
                  <code className="text-xs break-all">
                    {repositoryName(workload.values)}
                  </code>
                ),
              },
            ]
          : []),
        // What the rule narrows the repository to, if anything.
        ...(credential === "workload" && platformKey === GITHUB_ACTIONS_KEY
          ? githubActionsScopeRow(jobScope(workload.values))
          : []),
        // A GKE cluster by its name alone: the project and location are on
        // the step that asked for them, and the rail is too narrow for all
        // three.
        ...(credential === "workload" &&
        platformKey === "gke" &&
        (workload.values["cluster"] ?? "").trim() !== ""
          ? [
              {
                label: "Cluster",
                value: (
                  <code className="text-xs">
                    {workload.values["cluster"]?.trim()}
                  </code>
                ),
              },
            ]
          : []),
        ...(credential === "workload" &&
        platformKey === "gke" &&
        (workload.values["service_account"] ?? "").trim() !== ""
          ? [
              {
                label: "Service account",
                value: (
                  <span className="text-xs">
                    <code>{workload.values["service_account"]?.trim()}</code>
                    {(workload.values["namespace"] ?? "").trim() !== "" && (
                      <>
                        {" "}
                        in <code>{workload.values["namespace"]?.trim()}</code>
                      </>
                    )}
                  </span>
                ),
              },
            ]
          : []),
        ...(credential === "workload" &&
        platformKey === CUSTOM_PLATFORM_KEY &&
        (workload.values["issuer"] ?? "").trim() !== ""
          ? [
              {
                label: "Issuer",
                value: (
                  <code className="text-xs break-all">
                    {workload.values["issuer"]?.trim()}
                  </code>
                ),
              },
            ]
          : []),
      ]}
      highlight={highlight}
      // One line rather than every server listed: the rail would otherwise
      // name today's inventory for a choice that is deliberately not a list.
      servers={
        allServers
          ? [{ id: "all", name: "All servers", detail: "and ones added later" }]
          : selected.map((entry) => ({
              id: entry.server.id,
              name: entry.server.name,
              // A server with an issuer can still use a shared credential.
              detail:
                entry.server.issuerId &&
                connections.byServer.get(entry.server.id)?.status !==
                  "not-required"
                  ? "all tools, using an assigned connection"
                  : "all tools",
            }))
      }
    />
  );

  const body = () => {
    // Past the credential step, a federated agent is following its platform's
    // own setup, one of its steps per wizard step.
    if (workloadStep !== undefined) {
      return (
        <StepWorkload
          step={workloadStep}
          setup={workload}
          first={workloadStep === workload.steps[0]}
          platformName={platform?.displayName ?? "this platform"}
        />
      );
    }
    switch (step) {
      case 0:
        return (
          <div className="space-y-6">
            <WizardStepHeader
              title="Name"
              description="Every call this agent makes is recorded under this name. Pick one that says whose work it is doing, so an audit log entry or a live session is recognisable without looking the agent up."
            />
            <div className="max-w-xl space-y-2">
              <Label htmlFor="agent-name">Name</Label>
              <Input
                id="agent-name"
                value={name}
                onChange={(next) => {
                  setName(next);
                  setHighlight("Name");
                }}
                placeholder="Release Bot"
                maxLength={NAME_LIMIT}
                // An existing agent's name is its own while this flow is only
                // issuing it a key. Editing is the flow that may change it.
                disabled={existing && !editing}
                autoFocus={!existing}
              />
              {nameState === "taken" ? (
                <Text role="alert" warning small>
                  An agent named “{typedName}” already exists. Pick another
                  name.
                </Text>
              ) : (
                <Text muted small>
                  {existing
                    ? editing
                      ? "Shown in audit logs and session lists."
                      : "Rename this agent from its own page."
                    : "Shown in audit logs and session lists. You can rename it later."}
                </Text>
              )}
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-5">
            <StepServers
              servers={listedServers}
              isLoading={inventory.isLoading}
              isError={inventory.isError}
              onRetry={() => {
                inventory.refetch();
                // Coming back to the tab after asking for access is also the
                // moment the answer may have changed.
                recheck();
              }}
              scopeLabel={allServersScopeName}
              allServers={allServers}
              allServersRefusal={allServersRefusal}
              refusedServers={refusedServers}
              onRecheck={policyCheck.isError ? recheck : undefined}
              onAllServersChange={(next) => {
                setAllServers(next);
                setHighlight(next ? "server:all" : null);
              }}
              selected={selected}
              onChange={(next) => {
                setSelected(next);
                // The one just added, or the last one left when removing.
                const added = next.find(
                  (entry) =>
                    !selected.some((old) => old.server.id === entry.server.id),
                );
                setHighlight(
                  added === undefined ? null : `server:${added.server.id}`,
                );
              }}
            />
          </div>
        );
      case 2:
        return (
          <StepAuthentication
            servers={
              allServers ? listedServers : selected.map((entry) => entry.server)
            }
            connections={connections}
            allServers={allServers}
            onRemove={(id) => {
              if (allServers) {
                setAllServers(false);
                setSelected(
                  listedServers
                    .filter((server) => server.id !== id)
                    .map((server) => ({ server })),
                );
              } else
                setSelected(selected.filter((entry) => entry.server.id !== id));
            }}
          />
        );
      case 3:
        return (
          <div className="space-y-6">
            <WizardStepHeader
              title="Agent sign-in"
              description="Pick how the agent proves who it is to Speakeasy."
            />
            <SignInPath hop="agent" />
            <CredentialPicker
              credential={credential}
              platformKey={platformKey}
              entries={catalog.entries}
              isPending={catalog.isPending}
              isError={catalog.isError}
              onRetry={catalog.refetch}
              // Offered only to someone who could carry it out: a preset
              // trusts a platform and admits a subject for the organization.
              canFederate={canFederate}
              // Settled for an agent that exists: what it signs in with was
              // decided when it was registered, and changing it here would
              // mean removing a credential rather than editing one.
              disabled={editing}
              onChooseApiKey={() => {
                setCredential("api-key");
                setHighlight("Agent sign-in");
              }}
              onChoosePlatform={(key) => {
                setCredential("workload");
                setPlatformKey(key);
                setHighlight("Platform");
              }}
            />
          </div>
        );
      case 4:
        return (
          <StepInstall
            command={command}
            commandError={commandError}
            minting={minting}
            onRegenerate={() => {
              if (secret && gatewayURL) regenerate(secret, gatewayURL);
            }}
            onIssue={() => void provision()}
            issuing={provisioning || minting}
            secret={secret}
            gatewayURL={gatewayURL}
            serverCount={allServers ? "all" : selected.length}
          />
        );
      default:
        return (
          <StepVerify
            state={verify}
            firstCallAt={firstCallAt}
            gatewayURL={gatewayURL}
          />
        );
    }
  };

  const blocked = authStop
    ? authStop
    : step === 0
      ? !name.trim()
        ? "Name this agent."
        : nameState === "taken"
          ? "Pick a name no other agent has."
          : nameState === "checking"
            ? "Checking the name is free…"
            : null
      : step === 1
        ? !editing && !allServers && selected.length === 0
          ? "Select at least one MCP server."
          : refusedChoice
            ? refusedChoice
            : null
        : step === 2 &&
            offersConnections &&
            !allServers &&
            selected.some(
              (entry) =>
                entry.server.issuerId &&
                !connectionSelectable(
                  connections.byServer.get(entry.server.id),
                ),
            )
          ? "Connect or remove the servers that need authentication."
          : step === 2 && !editing && !allServers && selected.length === 0
            ? "Select at least one MCP server."
            : null;

  const footer = () => {
    // Editing writes after authentication. Past it the flow is the ordinary
    // one for an agent that exists: adding a credential it does not have.
    if (editing && step < 3) {
      return (
        <WizardFooter
          note={
            step === 0
              ? (blocked ??
                "Renaming an agent is recorded in the organization audit log.")
              : (authStop ??
                (authPending ? authPendingNote : null) ??
                refusedChoice ??
                (reconciled
                  ? "Changing what an agent reaches is recorded in the organization audit log."
                  : inventory.isError
                    ? "The server list could not be loaded, so the servers this agent reaches are not known. Retry the list before saving."
                    : "Reading the servers this agent reaches…"))
          }
          onBack={step === 0 ? undefined : () => setStep(step - 1)}
          primary={
            step < 2 ? (
              <Button disabled={!!blocked} onClick={() => setStep(step + 1)}>
                Continue
              </Button>
            ) : (
              <Button
                // Nothing may be written until the stored grants are in the
                // selection: the diff against an empty one withdraws them all.
                disabled={
                  saving ||
                  !reconciled ||
                  refusedChoice !== null ||
                  authStop !== null ||
                  authPending
                }
                onClick={() => void save()}
              >
                {saving ? "Saving…" : "Save changes and exit"}
              </Button>
            )
          }
        />
      );
    }
    if (workloadStep !== undefined) {
      const last = step - 4 === workload.steps.length - 1;
      const ready = workload.complete(workloadStep);
      return (
        <WizardFooter
          note={
            workloadStep.phase === "create"
              ? "Trusting a platform and allowing its access are recorded in the organization audit log."
              : workloadStep.phase === "connect"
                ? `Nothing here changes Speakeasy; these are ${platform?.displayName ?? "the platform"}'s own settings.`
                : "Nothing is created until the connect step."
          }
          onBack={() => setStep(step - 1)}
          primary={
            workloadStep.phase === "create" ? (
              // Already connected under this very rule: there is nothing to
              // write, so the step is a page to read and move past. Someone
              // who cannot manage workload identities only reads it.
              workload.connected || (editing && !canFederate) ? (
                <Button onClick={() => setStep(step + 1)}>Continue</Button>
              ) : (
                <Button
                  disabled={
                    workload.connecting || !workload.canConnect || !ready
                  }
                  onClick={() => {
                    void workload.connect().then((connected) => {
                      if (connected) setStep(step + 1);
                    });
                  }}
                >
                  {workload.connecting
                    ? editing
                      ? "Saving…"
                      : "Connecting…"
                    : editing
                      ? "Save changes"
                      : `Connect ${platform?.displayName ?? "platform"}`}
                </Button>
              )
            ) : last ? (
              <Button onClick={() => onDone(agentID ?? undefined)}>Done</Button>
            ) : (
              <Button disabled={!ready} onClick={() => setStep(step + 1)}>
                Continue
              </Button>
            )
          }
        />
      );
    }
    switch (step) {
      case 3:
        if (credential === "workload") {
          let note =
            "Choose the platform whose tokens this agent will present.";
          if (platformKey !== null) {
            note =
              workload.steps.length === 0
                ? "Setup is unavailable. Refresh or choose another option."
                : `${platform?.displayName ?? "Your platform"} issues the tokens; the next step sets it up.`;
          }
          // A preset opened from a link arrives chosen, even for someone who
          // may not trust a platform. Editing only reads the setup back.
          const refused = !editing && !canFederate;
          if (refused) {
            note =
              "Using a platform's tokens needs permission to manage workload identities. Choose an API key instead.";
          }
          return (
            <WizardFooter
              note={note}
              onBack={() => setStep(2)}
              primary={
                <Button
                  disabled={refused || workload.steps.length === 0}
                  onClick={() => setStep(4)}
                >
                  Continue
                </Button>
              }
            />
          );
        }
        return (
          <WizardFooter
            note={
              existing
                ? "Issuing a key is recorded in the organization audit log."
                : !project.id
                  ? "Reading the project this agent will belong to…"
                  : "Creating the agent is recorded in the organization audit log."
            }
            onBack={() => setStep(2)}
            primary={
              <Button
                // An agent belongs to the project it is made in, so there is
                // nothing to create until that project has resolved.
                disabled={provisioning || (!existing && !project.id)}
                onClick={() => void provision()}
              >
                {provisioning
                  ? existing
                    ? "Issuing…"
                    : "Creating…"
                  : existing
                    ? "Issue key"
                    : "Create agent"}
              </Button>
            }
          />
        );
      case 4:
        return (
          <WizardFooter
            note="The API key can be rotated or revoked from the agent page."
            primary={
              <Button onClick={() => setStep(5)}>
                Continue to verification
              </Button>
            }
          />
        );
      case 5:
        return (
          <WizardFooter
            note={
              verify === "connected"
                ? "The agent has called the gateway."
                : "Run the setup from the previous step, then wait for the first call."
            }
            onBack={() => setStep(4)}
            primary={
              <Button onClick={() => onDone(agentID ?? undefined)}>
                {verify === "connected" ? "Done" : "Skip for now"}
              </Button>
            }
          />
        );
      default:
        return (
          <WizardFooter
            note={
              blocked ??
              (step === 0
                ? existing
                  ? "This agent's identity is settled; this flow issues it a key."
                  : "You can rename it later."
                : allServers
                  ? // Not a count: the choice is deliberately not a list, and
                    // naming today's would be a number that changes behind it.
                    "All servers selected."
                  : `${selected.length} of ${listedServers.length} servers selected.`)
            }
            onBack={step > 0 ? () => setStep(step - 1) : undefined}
            primary={
              <Button disabled={!!blocked} onClick={() => setStep(step + 1)}>
                Continue
              </Button>
            }
          />
        );
    }
  };

  return (
    <div className="space-y-6">
      <WizardStepper
        steps={stepNames}
        current={step}
        // While creating, only a step already completed is a destination: the
        // ones ahead are built from choices not yet made. Provisioning an
        // existing agent has no such order, so every step is reachable until
        // the key is issued.
        // Editing has settled everything a credential step would ask.
        settledFrom={editing ? 3 : undefined}
        forward={existing && !secret}
        // Verify watches a key being used, so it has nothing to watch until
        // one is issued. Without this an existing agent could jump straight
        // to it and finish the flow having provisioned nothing.
        onJump={(next) =>
          setStep(
            secret || credential === "workload" ? next : Math.min(next, 4),
          )
        }
      />
      {error && (
        <p
          role="alert"
          className="border-destructive text-destructive border p-3 text-sm"
        >
          {error}
        </p>
      )}
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_260px] lg:gap-16">
        <div>{body()}</div>
        {/* The cell stretches to the row's height on purpose: a sticky child
            can only travel inside its own containing block, so a cell shrunk
            to the rail's height gives it nowhere to go and it scrolls away
            with the page. */}
        <div className="lg:order-last">{summary}</div>
      </div>
      {footer()}
    </div>
  );
}
